import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AllocationRound, HousingApplication, HousingUnit } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatusBadge, TabBar } from '../../ui/components';

const UNIT_TYPES = ['STUDIO', 'ONE_BED', 'TWO_BED', 'THREE_BED', 'SERVICED_LAND'] as const;

export function AllocationRounds() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isOfficer = roles.includes('HOUSING_OFFICER');
  const navigate = useNavigate();
  const [tab, setTab] = useState<'rounds' | 'units'>('rounds');
  const [district, setDistrict] = useState('');
  const [status, setStatus] = useState('');
  const [showNewRound, setShowNewRound] = useState(false);
  const [showNewUnit, setShowNewUnit] = useState(false);

  const roundsPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (district) params.set('district', district);
    if (status) params.set('status', status);
    return `/api/v1/housing/allocation-rounds?${params.toString()}`;
  }, [district, status]);
  const rounds = useCursorList<AllocationRound>(tab === 'rounds' ? roundsPath : null);

  const unitsPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '50');
    if (district) params.set('district', district);
    return `/api/v1/housing/units?${params.toString()}`;
  }, [district]);
  const units = useCursorList<HousingUnit>(tab === 'units' && isOfficer ? unitsPath : null);

  const markBuilt = async (unitId: string) => {
    try {
      await api.post(`/api/v1/housing/units/${unitId}/status`, { status: 'BUILT' });
      units.reload();
    } catch {
      // surfaced inline via the row itself failing to update; list stays as-is
    }
  };

  return (
    <div>
      <PageHeader
        title="Allocation"
        subtitle="Allocation rounds snapshot a district's live QUEUED pool for committee decision; housing units are the physical stock rounds draw from."
        actions={
          tab === 'rounds' && isOfficer ? (
            <button className="btn-primary" onClick={() => setShowNewRound(true)}>+ New round</button>
          ) : tab === 'units' && isOfficer ? (
            <button className="btn-primary" onClick={() => setShowNewUnit(true)}>+ Register unit</button>
          ) : undefined
        }
      />

      <TabBar tabs={[{ key: 'rounds', label: 'Allocation rounds' }, { key: 'units', label: 'Housing units' }]} active={tab} onChange={setTab} />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="District" className="w-48">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Anse Royale" />
        </Field>
        {tab === 'rounds' ? (
          <Field label="Status" className="w-44">
            <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {['DRAFT', 'OPEN', 'COMMITTEE', 'PUBLISHED', 'CLOSED'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        ) : null}
      </div>

      {tab === 'rounds' ? (
        <>
          {rounds.error ? <div className="mb-4"><ErrorNote message={rounds.error} /></div> : null}
          <div className="card overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr>
                  <th className="table-head">District</th>
                  <th className="table-head">Status</th>
                  <th className="table-head">Units offered</th>
                </tr>
              </thead>
              <tbody>
                {rounds.items.map((r) => (
                  <tr key={r.id} className="table-row-link" onClick={() => navigate(`/housing/allocation/${r.id}`)}>
                    <td className="table-cell font-semibold text-slate-800">{r.district}</td>
                    <td className="table-cell"><StatusBadge status={r.status} /></td>
                    <td className="table-cell">{r.unitIds.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rounds.loading ? <Spinner label="Loading rounds…" /> : null}
            {!rounds.loading && rounds.items.length === 0 ? <EmptyState title="No allocation rounds found" /> : null}
          </div>
          <LoadMoreButton nextCursor={rounds.nextCursor} loading={rounds.loading} onClick={rounds.loadMore} />
        </>
      ) : (
        <>
          {!isOfficer ? <ErrorNote message="Requires HOUSING_OFFICER to view the units register." /> : null}
          {units.error ? <div className="mb-4"><ErrorNote message={units.error} /></div> : null}
          {isOfficer ? (
            <div className="card overflow-x-auto">
              <table className="w-full min-w-[600px] border-collapse">
                <thead>
                  <tr>
                    <th className="table-head">Project</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Address</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">Status</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {units.items.map((u) => (
                    <tr key={u.id}>
                      <td className="table-cell font-semibold text-slate-800">{u.project}</td>
                      <td className="table-cell">{u.district}</td>
                      <td className="table-cell text-xs text-slate-500">{u.address}</td>
                      <td className="table-cell text-xs">{u.type.replaceAll('_', ' ')}</td>
                      <td className="table-cell"><StatusBadge status={u.status} /></td>
                      <td className="table-cell text-right">
                        {u.status === 'PLANNED' ? (
                          <button className="btn-secondary btn-xs" onClick={() => markBuilt(u.id)}>Mark BUILT</button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {units.loading ? <Spinner label="Loading units…" /> : null}
              {!units.loading && units.items.length === 0 ? <EmptyState title="No housing units registered" /> : null}
            </div>
          ) : null}
          <LoadMoreButton nextCursor={units.nextCursor} loading={units.loading} onClick={units.loadMore} />
        </>
      )}

      {showNewRound ? (
        <NewRoundModal onClose={() => setShowNewRound(false)} onCreated={(r) => { setShowNewRound(false); navigate(`/housing/allocation/${r.id}`); }} />
      ) : null}
      {showNewUnit ? <NewUnitModal onClose={() => setShowNewUnit(false)} onCreated={() => { setShowNewUnit(false); units.reload(); }} /> : null}
    </div>
  );
}

function NewRoundModal({ onClose, onCreated }: { onClose: () => void; onCreated: (r: AllocationRound) => void }) {
  const [district, setDistrict] = useState('');
  const [builtUnits, setBuiltUnits] = useState<HousingUnit[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!district) {
      setBuiltUnits([]);
      return;
    }
    const params = new URLSearchParams({ district, status: 'BUILT', limit: '100' });
    api
      .get<{ items: HousingUnit[] }>(`/api/v1/housing/units?${params.toString()}`)
      .then((r) => setBuiltUnits(r.items))
      .catch(() => setBuiltUnits([]));
  }, [district]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const round = await api.post<AllocationRound>('/api/v1/housing/allocation-rounds', { district, unitIds: [...selected] });
      onCreated(round);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not create round');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New allocation round" onClose={onClose} wide>
      <p className="mb-3 text-xs text-slate-500">Only BUILT units in the chosen district can be offered — the API rejects PLANNED/ALLOCATED/OCCUPIED units.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <Field label="District">
        <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Anse Royale" />
      </Field>
      <div className="mt-3">
        <div className="field-label">BUILT units available ({builtUnits.length})</div>
        <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
          {builtUnits.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-400">Enter a district with registered BUILT units.</div>
          ) : (
            builtUnits.map((u) => (
              <label key={u.id} className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-0 hover:bg-slate-50">
                <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} />
                <span className="font-medium text-slate-800">{u.project}</span>
                <span className="text-xs text-slate-500">{u.address} · {u.type.replaceAll('_', ' ')}</span>
              </label>
            ))
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !district || selected.size === 0} onClick={submit}>
          {busy ? 'Creating…' : `Create round with ${selected.size} unit(s)`}
        </button>
      </div>
    </Modal>
  );
}

function NewUnitModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [project, setProject] = useState('');
  const [district, setDistrict] = useState('');
  const [address, setAddress] = useState('');
  const [type, setType] = useState<(typeof UNIT_TYPES)[number]>('ONE_BED');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/housing/units', { project, district, address, type });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not register unit');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register housing unit" onClose={onClose}>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-3">
        <Field label="Project name">
          <input className="field-input" value={project} onChange={(e) => setProject(e.target.value)} />
        </Field>
        <Field label="District">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="Address">
          <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field label="Type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            {UNIT_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !project || !district || !address} onClick={submit}>
          {busy ? 'Registering…' : 'Register unit'}
        </button>
      </div>
    </Modal>
  );
}

export function AllocationRoundDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isOfficer = roles.includes('HOUSING_OFFICER');
  const isCommittee = roles.includes('ALLOCATION_COMMITTEE');
  const roundQ = useApiQuery<AllocationRound>(`/api/v1/housing/allocation-rounds/${id}`);
  const [enriched, setEnriched] = useState<Record<string, HousingApplication>>({});
  const [decided, setDecided] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decidingApp, setDecidingApp] = useState<string | null>(null);
  const [decision, setDecision] = useState<'ALLOCATE' | 'WAITLIST' | 'REJECT'>('ALLOCATE');
  const [unitId, setUnitId] = useState('');
  const [reason, setReason] = useState('');
  const [blind, setBlind] = useState(false);

  const round = roundQ.data;
  const pool = useMemo(() => round?.eligiblePoolSnapshot ?? [], [round]);

  // M19 has no GET endpoint listing AllocationEntry rows for a round (only
  // the OPEN-time eligiblePoolSnapshot JSON of {applicationId, rank, score}
  // — see allocation.ts). To make the committee's decide screen usable this
  // enriches each pool entry with its live application (NIN, district) via
  // individual GET /applications/:id calls, real requests against the real
  // per-application endpoint rather than a fabricated batch lookup.
  useEffect(() => {
    if (pool.length === 0) return;
    let alive = true;
    Promise.all(
      pool.slice(0, 60).map((p) =>
        api
          .get<HousingApplication>(`/api/v1/housing/applications/${p.applicationId}`)
          .then((a) => [p.applicationId, a] as const)
          .catch(() => null),
      ),
    ).then((results) => {
      if (!alive) return;
      const map: Record<string, HousingApplication> = {};
      for (const r of results) if (r) map[r[0]] = r[1];
      setEnriched(map);
    });
    return () => {
      alive = false;
    };
  }, [pool]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      roundQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (applicationId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/housing/allocation-rounds/${id}/decide`, {
        applicationId,
        decision,
        unitId: decision === 'ALLOCATE' ? unitId : undefined,
        reason: reason || undefined,
        blind,
      });
      setDecided((prev) => new Set(prev).add(applicationId));
      setDecidingApp(null);
      setReason('');
      setUnitId('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record decision');
    } finally {
      setBusy(false);
    }
  };

  if (roundQ.loading) return <Spinner label="Loading round…" />;
  if (roundQ.error || !round) return <ErrorNote message={roundQ.error ?? 'Round not found'} />;

  return (
    <div>
      <BackLink to="/housing/allocation" />
      <PageHeader title={`Round — ${round.district}`} actions={<StatusBadge status={round.status} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      <p className="mb-4 text-xs text-slate-500">
        No GET endpoint lists live AllocationEntry decisions for a round — the checkmarks below reflect decisions made
        in this browser session only; every decision is still recorded correctly server-side.
      </p>

      <div className="mb-5 flex flex-wrap gap-2">
        {isOfficer && round.status === 'DRAFT' ? (
          <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/allocation-rounds/${id}/open`))}>
            Open round (snapshot live queue)
          </button>
        ) : null}
        {isCommittee && round.status === 'OPEN' ? (
          <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/allocation-rounds/${id}/convene`))}>
            Convene committee
          </button>
        ) : null}
        {isOfficer && round.status === 'COMMITTEE' ? (
          <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/allocation-rounds/${id}/publish`))}>
            Publish results
          </button>
        ) : null}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Rank</th>
              <th className="table-head">Score</th>
              <th className="table-head">Applicant</th>
              <th className="table-head">District</th>
              <th className="table-head" />
            </tr>
          </thead>
          <tbody>
            {pool.map((p) => (
              <tr key={p.applicationId}>
                <td className="table-cell font-semibold text-slate-800">#{p.rank}</td>
                <td className="table-cell tabular-nums">{p.score}</td>
                <td className="table-cell">{enriched[p.applicationId]?.nin ?? p.applicationId}</td>
                <td className="table-cell text-xs text-slate-500">{enriched[p.applicationId]?.districtPreference ?? '—'}</td>
                <td className="table-cell text-right">
                  {decided.has(p.applicationId) ? (
                    <span className="text-xs font-semibold text-emerald-600">Decided this session</span>
                  ) : isCommittee && round.status === 'COMMITTEE' ? (
                    <button className="btn-secondary btn-xs" onClick={() => setDecidingApp(p.applicationId)}>Decide</button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pool.length === 0 ? <EmptyState title="Round has not been opened yet" hint="Open the round to snapshot the live QUEUED pool for this district." /> : null}
      </div>

      {decidingApp ? (
        <Modal title={`Decide — ${enriched[decidingApp]?.nin ?? decidingApp}`} onClose={() => setDecidingApp(null)}>
          <div className="space-y-3">
            <Field label="Decision">
              <select className="field-input" value={decision} onChange={(e) => setDecision(e.target.value as typeof decision)}>
                <option value="ALLOCATE">Allocate</option>
                <option value="WAITLIST">Waitlist</option>
                <option value="REJECT">Reject</option>
              </select>
            </Field>
            {decision === 'ALLOCATE' ? (
              <Field label="Unit id (must be one of this round's offered units)">
                <select className="field-input" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  <option value="">Select a unit…</option>
                  {round.unitIds.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
            ) : null}
            <Field label="Reason (optional)">
              <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={blind} onChange={(e) => setBlind(e.target.checked)} />
              Committee saw rank/score only (blind decision)
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setDecidingApp(null)}>Cancel</button>
            <button className="btn-primary" disabled={busy || (decision === 'ALLOCATE' && !unitId)} onClick={() => decide(decidingApp)}>
              Save decision
            </button>
          </div>
        </Modal>
      ) : null}

      {round.publishedAt ? (
        <p className="mt-4 text-xs text-slate-500">Published {formatDateTime(round.publishedAt)} — notice {round.publishedNoticeRef}</p>
      ) : null}
    </div>
  );
}
